<template>
  <div
    v-if="initialized"
    :style="environmentBorderStyle"
    class="h-full w-full"
  >
    <AccessRevokedPage v-if="block_emp" />
    <div
      v-else
      class="h-full w-full"
    >
      <Banner />
      <router-view />
    </div>
  </div>
  <div v-else-if="$route.name === ROUTE_COMMON.LOGIN || $route.name === ROUTE_COMMON.NOTIFICATIONS_REDIRECT">
    <router-view />
  </div>
  <b-loading
    v-else
    :model-value="true"
  />
  <Toast />
  <GlobalConfirmModal />
</template>

<script setup>
/* oxlint-disable max-lines */
import Banner from '@/app/shared_components/Banner.vue';
import AccessRevokedPage from '@/app/_employees/pages/AccessRevokedPage.vue';
import {
  PROFILE_TYPES,
  Tenant,
  TENANT_PICTURE_FAVICON_URL,
  TENANT_PRIMARY_COLOR_DEFAULT,
} from '@/app/data/model_constants';
import {
  PROFILE_TO_ROUTES,
  PROFILE_TO_START_ROUTE,
  ROUTE_COMMON,
  ROUTES_PARENT_DOMAIN_REGEX,
  ROUTES_USERS,
} from '@/app/data/route_constants';
import * as Sentry from '@sentry/vue';
import {
  ENVIRONMENT_COLORS,
  ENVIRONMENT_FAVICONS,
  LOGIN_PAGE_NOTIFICATIONS,
  VITE_ENV_DEVELOPMENT,
  VITE_ENV_PRODUCTION,
  VITE_ENV_REVIEW,
} from '@/app/data/general_constants';
import { FT } from '@/app/data/flipper_constants';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { ZIGGU_PARENT_URL, zigguProfileSubdomain } from '@/app/util/urls';
import { useMqService } from '@/plugins/mq';
import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, watch } from 'vue';
import useProfile from '@/app/composables/useProfile';
import useImpersonation from '@/app/util/impersonation';
import useZoomPrevention from '@/app/composables/useZoomPrevention';
import { useI18n } from 'vue-i18n';
import applyTheme from '@/app/util/theme_functions';
import runFilterUndefinedMigration from '@/app/util/migrations/filter_undefined_migration';
import { useToasts } from '@/app/util/toasts';
import INJECT_KEYS from '@/app/data/inject_keys';

const mq = useMqService();
const { t, te, locale } = useI18n();
const { clearImpersonationParams, stopImpersonation, impersonationURL, supervisorURL } = useImpersonation();
const { disableZoom, cleanupZoomPrevention } = useZoomPrevention();
const { toast_action_failed } = useToasts();

const store = useStore();
const route = useRoute();
const router = useRouter();
const emitter = inject(INJECT_KEYS.EMITTER);
const flipper = inject(INJECT_KEYS.FLIPPER);
const instance = getCurrentInstance();

const {
  current_client,
  current_profile,
  current_profile_true,
  fetchClient,
  fetchFeatureToggles,
  fetchProfile,
  initialized,
} = useProfile();

const { current_is_impersonation, current_tenant, current_user } = mapGetters([
  'current_is_impersonation',
  'current_tenant',
  'current_user',
]);

const { FETCH_FEATURE_TOGGLES, FETCH_USER_PROFILE, LOGOUT_USER, UPDATE_EMAIL_ADDRESS_RESET } = mapActions([
  'FETCH_FEATURE_TOGGLES',
  'FETCH_USER_PROFILE',
  'LOGOUT_USER',
  'UPDATE_EMAIL_ADDRESS_RESET',
]);

// direct read, not mapGetters: role-scoped getter, absent until initStore registers it
const billings = computed(() => store.getters.billings);

const authenticated = computed(() => instance.proxy.$auth.is_authenticated);

const is_parent_domain = computed(() => ZIGGU_PARENT_URL === window.location.origin);

const billing_client = computed(() => {
  if (!current_client.value) return {};

  return _.find(billings.value, ['attributes.client_id', current_client.value.id]);
});

const block_emp = computed(
  () =>
    _.get(current_profile.value, 'type') === PROFILE_TYPES.EMPLOYEE &&
    _.get(current_client.value, 'attributes.revoke_access_emp'),
);

const environmentBorderStyle = computed(() => {
  // No border in production: it's the customer-facing environment.
  const color = ENVIRONMENT_COLORS[import.meta.env.VITE_ENV];
  if (!color || import.meta.env.VITE_ENV === VITE_ENV_PRODUCTION) return {};
  return { borderTop: `8px solid ${color}` };
});

function initTheme() {
  // Supervisors are internal and hop between tenants, so they keep Ziggu's own colors.
  const is_supervisor = _.get(current_profile.value, 'type') === PROFILE_TYPES.SUPERVISOR;
  const tenant_color = is_supervisor
    ? TENANT_PRIMARY_COLOR_DEFAULT
    : _.get(current_tenant.value, 'attributes.theme_primary_color');
  applyTheme(tenant_color);
}

async function logout() {
  // logout trigger during impersonation stops impersonation
  if (current_is_impersonation.value) {
    await stopImpersonation();
  } else {
    clearImpersonationParams();
    // retrieve and append any (present) ziggu parameters to pass onto login page
    let subdomain;
    // both no profile (TLD) and partner profile redirect back to TLD
    if (!current_profile.value || current_profile.value.type === PROFILE_TYPES.PARTNER) {
      subdomain = null;
      // other profiles redirect back to subdomain URL
    } else {
      subdomain = zigguProfileSubdomain(current_profile.value, current_tenant.value);
    }
    const email = _.get(current_user.value, 'attributes.email_address', '');
    const notification = LOGIN_PAGE_NOTIFICATIONS.SUCCESS_LOGOUT;
    window.Intercom('shutdown');
    await LOGOUT_USER({});
    const params = [];
    if (subdomain) params.push(`subdomain=${subdomain}`);
    if (email) params.push(`email=${encodeURIComponent(email)}`);
    if (notification) params.push(`notification=${notification}`);
    let url = `${ZIGGU_PARENT_URL}/login`;
    if (params.length > 0) url += `?${params.join('&')}`;
    window.location.href = url;
  }
}

async function handleErrorUnauthorized() {
  // illegal route navigation to other profile type routes
  if (
    current_profile.value &&
    route &&
    route.name &&
    !route.name.startsWith(current_profile.value.type.toLowerCase())
  ) {
    await initUser();
    // unauthorized error after init: profile does not exist for given tenant or is not invited/active
    //  redirect back to parent domain name
  } else {
    // remove possible impersonation meta params, to prevent endless loop
    clearImpersonationParams();
    // parent domain on user namespace: no profile exists for this user, force logout
    if (
      is_parent_domain.value &&
      !window.location.pathname.startsWith('/employee') &&
      !window.location.pathname.startsWith('/partner')
    ) {
      // retrieve and append any (present) ziggu parameters to pass onto login page
      const email = _.get(current_user.value, 'attributes.email_address', '');
      const notification = LOGIN_PAGE_NOTIFICATIONS.ERROR_NO_ACCESS;
      await LOGOUT_USER({});
      const params = [];
      if (email) params.push(`email=${encodeURIComponent(email)}`);
      if (notification) params.push(`notification=${notification}`);
      let url = `${ZIGGU_PARENT_URL}/login`;
      if (params.length > 0) url += `?${params.join('&')}`;
      window.location.href = url;
      // subdomain: no profile for user on this subdomain, redirect to parent domain
    } else {
      window.location.href = ZIGGU_PARENT_URL;
    }
  }
}

function handleErrorForbidden() {
  toast_action_failed();
}

function handleErrorForbiddenImpersonation() {
  const message = t('toast.action.impersonate_unauthorized');
  toast_action_failed({ message });
}

function handleErrorInternalServer() {
  toast_action_failed();
}

function handleErrorNotFound() {
  // app needs to be initialized to render 404-page
  if (initialized.value) {
    router.replace({ name: ROUTE_COMMON.ERROR_NOT_FOUND });
    // app not initialized (FETCH_USER_PROFILE call likely gave a 404), redirect back to root of TLD
  } else {
    // remove possible impersonation meta params, to prevent endless loop
    clearImpersonationParams();
    window.location.href = window.location.origin;
  }
}

function handleErrorUnprocessable(error) {
  const messages = Array.isArray(error) ? error : [error];
  const messages_known = messages.filter((message) => te(`toast.action.${message}`));
  if (messages_known.length > 0) {
    toast_action_failed({ message: messages_known.map((message) => t(`toast.action.${message}`)).join(' ') });
    return;
  }
  toast_action_failed({ message: t('toast.action.unprocessable', { error: messages.join(', ') }) });
}

async function initUser() {
  initialized.value = false;
  await FETCH_USER_PROFILE({});
  initTheme();
  // parent domain in user namespace
  if (is_parent_domain.value && !ROUTES_PARENT_DOMAIN_REGEX.test(window.location.pathname)) {
    initialized.value = true;
    router.replace({ name: ROUTES_USERS.DASHBOARD });
    // employee/partner namespace via subdomain => parent domain redirect, keep path (no longer supported since PWA)
  } else if (!is_parent_domain.value && ROUTES_PARENT_DOMAIN_REGEX.test(window.location.pathname)) {
    const url_redirect = `${ZIGGU_PARENT_URL}${window.location.pathname}${window.location.search}`;
    window.location.href = url_redirect;
    // customer namespace (subdomain)
  } else {
    await FETCH_FEATURE_TOGGLES({});
    initSentry();
    await initStore();

    const { language } = current_profile.value.attributes;
    locale.value = language;
    initialized.value = true;

    const { email_address_reset_token } = route.query;
    if (email_address_reset_token) confirmEmailChange(email_address_reset_token);

    const profile_type = current_profile.value.type;
    const profile_routes = PROFILE_TO_ROUTES[profile_type];
    if (
      route.name &&
      (!profile_routes.includes(route.name) || route.name.toLowerCase().includes('root')) &&
      ROUTE_COMMON.NOTIFICATIONS_REDIRECT !== route.name
    ) {
      const route_start = PROFILE_TO_START_ROUTE[profile_type];
      let params = {};
      if (profile_type !== PROFILE_TYPES.SUPERVISOR) {
        const subdomain = current_tenant.value.attributes.subdomain;
        params = { subdomain };
        if (profile_type === PROFILE_TYPES.CUSTOMER || current_tenant.value.attributes.status === Tenant.STATUS_TEST) {
          // customers access platform via client subdomain
          await router.replace({ name: route_start, params });
        } else if (window.location.origin === ZIGGU_PARENT_URL) {
          await router.replace({ name: route_start, params });
        } else {
          // employees & partners: enforce access via top level domain (consistency with PWA)
          const url_path = router.resolve({ name: route_start, params }).href;
          window.location.href = `${ZIGGU_PARENT_URL}${url_path}`;
        }
      } else {
        await router.replace({ name: route_start, params });
      }
    }
  }
}

async function confirmEmailChange(email_address_reset_token) {
  if (!email_address_reset_token) return;
  try {
    await UPDATE_EMAIL_ADDRESS_RESET({ email_address_reset_token });
    emitter.$emit('banner', { message: t('profile.labels.updateEmailSuccessful'), color: 'success' });
    const query = { ...route.query };
    delete query.email_address_reset_token;
    router.replace(route.path, { query });
  } catch {
    emitter.$emit('banner', { message: t('profile.labels.updateEmailFailed'), color: 'danger' });
    throw new Error('user failed to update email');
  }
}

function initIntercom() {
  // disable for test clients and for actual customers
  if (
    _.get(current_client.value, 'attributes.status') === Tenant.STATUS_TEST ||
    current_profile_true.value.type === PROFILE_TYPES.CUSTOMER
  )
    return;

  // see https://developers.intercom.com/installing-intercom/docs/javascript-api-attributes-objects
  let options = {};
  // development environment fallbacks to dummy 'Demo User', regardless of profile
  if (import.meta.env.VITE_ENV === VITE_ENV_DEVELOPMENT) {
    options = {
      user_id: 'demo_user_id',
      user_hash: '97a94ce0babc3f5e78aa530c9f481b54b07dd658da28be58560a7218bfcd1f87',
      name: 'Demo User',
    };
  } else {
    let impersonation_url;
    let supervisor_url;
    try {
      impersonation_url = impersonationURL(current_profile_true.value, current_client.value);
      supervisor_url = supervisorURL(current_profile_true.value);
    } catch (error) {
      impersonation_url = '';
      supervisor_url = '';
    }
    options = {
      impersonation_url,
      supervisor_url,
      intercom_user_jwt: current_user.value.attributes.intercom_user_jwt,
      user_id: current_user.value.attributes.intercom_user_id,
      name: current_profile_true.value.attributes.name,
      email: current_profile_true.value.attributes.email_address,
      first_name: current_profile_true.value.attributes.first_name,
      last_name: current_profile_true.value.attributes.last_name,
      language: current_profile_true.value.attributes.language,
      package: _.get(current_client.value, 'attributes.package'),
      phone_mobile: _.get(current_profile_true.value, 'attributes.phone_mobile'),
      phone_landline: _.get(current_profile_true.value, 'attributes.phone_landline'),
      profile_type: current_profile_true.value.type,
      client_id: _.get(current_client.value, 'id'),
      subdomain: _.get(current_profile.value, 'attributes.subdomain'),
      arr: _.get(billing_client.value, 'attributes.arr'),
      avatar: { type: 'avatar', image_url: current_profile_true.value.attributes.avatar_url },
      profiles_count: current_profile_true.value.attributes.profiles_count,
    };
    // add company data for employee and partner profiles
    if (current_profile_true.value.type === PROFILE_TYPES.EMPLOYEE) {
      // employee profile's 'company' represents client's specifics
      options.company = {
        company_id: current_client.value.attributes.subdomain,
        company_status: current_client.value.attributes.status,
        created_at: Math.floor(Date.parse(current_client.value.attributes.created_at) / 1000),
        name: current_client.value.attributes.name,
      };
      options.role = current_profile_true.value.attributes.role;
      options.profession = current_profile_true.value.attributes.profession;
      options.grant_impersonation = current_profile_true.value.attributes.grant_impersonation;
    } else if (current_profile_true.value.type === PROFILE_TYPES.PARTNER) {
      // partner profile's 'company' represents actual partner's company (with email address domain as unique id)
      const email_address = current_profile_true.value.attributes.email_address;
      const company_id = email_address ? email_address.split('@')[1] : 'unknown';
      options.company = {
        company_id,
        company_status: current_client.value.attributes.status,
        created_at: Math.floor(Date.parse(current_client.value.attributes.created_at) / 1000),
        name: company_id,
      };
    }
  }
  // hide launcher when impersonating customers to avoid any confusion
  if (current_profile.value.type === PROFILE_TYPES.CUSTOMER) {
    options.hide_default_launcher = true;
  }
  options.ft_mobile_app_customer = flipper.enabled(FT.MOBILE_APP_CUSTOMER);
  window.Intercom('boot', {
    app_id: 'kqlc8dey',
    hide_default_launcher: true,
    ...options,
  });
}

function initSentry() {
  const scope = Sentry.getCurrentScope();
  scope.setUser({
    id: current_profile_true.value.attributes.user_id,
    profile_name: current_profile.value.attributes.name,
    profile_true_name: current_profile_true.value.attributes.name,
  });
  const { client_id } = current_profile.value.attributes;
  if (client_id) scope.setTag('client_id', client_id);
  scope.setTag('profile_tid', `${current_profile.value.type}#${current_profile.value.id}`);
  scope.setTag('profile_true_tid', `${current_profile_true.value.type}#${current_profile_true.value.id}`);
  if (import.meta.env.VITE_ENV === VITE_ENV_REVIEW) {
    scope.setTag('pull_request_id', _.parseInt(import.meta.env.VITE_PULL_REQUEST_ID));
  }
}

async function initStore() {
  // dynamic import of static pathnames
  if (!current_profile.value) return;
  const profile_store = await (() => {
    switch (current_profile.value.type) {
      case PROFILE_TYPES.CUSTOMER:
        return import('@/store/customers/index.js');
      case PROFILE_TYPES.EMPLOYEE:
        return import('@/store/employees/index.js');
      case PROFILE_TYPES.PARTNER:
        return import('@/store/partners/index.js');
      case PROFILE_TYPES.SUPERVISOR:
        return import('@/store/supervisors/index.js');
      default:
        return null;
    }
  })();
  if (!profile_store) return;
  const module_name = `${current_profile.value.type.toLowerCase()}s`;
  await store.registerModule(module_name, profile_store.default);
  store.dispatch('ASSIGN_CURRENT_PROFILE', { profile: current_profile.value });
}

function initPWA() {
  if (flipper.enabled(FT.MOBILE_APP_CUSTOMER)) {
    const settings = {
      name: current_tenant.value.attributes.name,
      icon512: current_tenant.value.attributes.picture_logo_url_pwa,
      themeColor: current_tenant.value.attributes.theme_primary_color,
      buttonColor: current_tenant.value.attributes.theme_primary_color,
    };
    settings.uid = window.location.hostname;
    window.progressierAppRuntimeSettings = settings;
    const script = document.createElement('script');
    script.src = 'https://progressier.app/mmTtP8WpP8clXzlTCZSV/script.js';
    script.defer = true;
    document.querySelector('head').appendChild(script);
  }
}

function initFavicon(picture_favicon_url) {
  let link = document.querySelector('link[rel~="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  // Per-environment favicon so dev/staging/review tabs are easy to tell apart from production.
  const env_favicon = ENVIRONMENT_FAVICONS[import.meta.env.VITE_ENV] ?? ENVIRONMENT_FAVICONS[VITE_ENV_PRODUCTION];
  const has_custom_favicon = picture_favicon_url && picture_favicon_url !== TENANT_PICTURE_FAVICON_URL;
  const use_custom = has_custom_favicon && import.meta.env.VITE_ENV === VITE_ENV_PRODUCTION;
  link.setAttribute('href', use_custom ? picture_favicon_url : env_favicon);
}

function initTitle() {
  if (route.name.includes('supervisor')) {
    document.title = 'Supervisor';
    return;
  }
  let title = '';
  const title_old = document.title;
  // append notifications counter from old title
  if (title_old.startsWith('(')) {
    const notifications_counter = title_old.split(' ')[0];
    title += `${notifications_counter} `;
  }
  // append route name
  const route_name_i18n = t(`window.${route.name}`);
  if (!route_name_i18n.endsWith(route.name)) {
    title += ` ${route_name_i18n}`;
  }
  // append current_tenant name
  if (current_tenant.value) {
    title += ` - ${current_tenant.value.attributes.name}`;
  } else if (current_profile.value && current_profile.value.type === PROFILE_TYPES.SUPERVISOR) {
    title += 'Ziggu';
  }
  document.title = title;
}

watch(
  authenticated,
  async (value) => {
    if (!value) return;
    // notification redirect page skips regular init flow (uses URL nav <-> of router nav: forces re-init)
    if (window.location.pathname.startsWith('/notifications')) return;
    await initUser();
  },
  { immediate: true },
);

watch(
  route,
  () => {
    if (initialized.value) initTitle();
  },
  { immediate: true },
);

runFilterUndefinedMigration();

// used only once to get the buefy modal context issue working.
// would not recommend to use this another time
instance.appContext.app.config.globalProperties.mq = mq;

// general handler (also used by login page)
emitter.$on('initFavicon', initFavicon);
emitter.$on('initTheme', initTheme);

// user handler
emitter.$on('initUser', initUser);

// profile handlers
emitter.$on('fetchClient', fetchClient);
emitter.$on('fetchFeatureToggles', fetchFeatureToggles);
emitter.$on('fetchProfile', fetchProfile);
emitter.$on('initIntercom', initIntercom);
emitter.$on('initTitle', initTitle);
emitter.$on('logout', logout);
emitter.$on('initPWA', initPWA);

// add impersonation meta attributes in sessionStorage, if present in URL
const params = new URL(document.location).searchParams;
const profile_type = params.get('_profile_type');
const profile_id = params.get('_profile_id');
const url_return_to = params.get('_return_to');
if (profile_type && profile_id) {
  sessionStorage.setItem('_profile_type', profile_type);
  sessionStorage.setItem('_profile_id', profile_id);
}
if (url_return_to) {
  sessionStorage.setItem('_impersonation_return_to', url_return_to);

  const url = new URL(window.location.href);
  url.searchParams.delete('_return_to');
  window.history.replaceState(window.history.state, document.title, url.toString());
}

// api error handlers (for all routes except /login, ignored via api/index.js, login page handles it separately)
emitter.$on('errors:401', handleErrorUnauthorized);
emitter.$on('errors:403', handleErrorForbidden);
emitter.$on('errors:404', handleErrorNotFound);
emitter.$on('errors:422', handleErrorUnprocessable);
emitter.$on('errors:500', handleErrorInternalServer);
emitter.$on('errors:418', handleErrorForbiddenImpersonation);

onMounted(() => {
  // Apply zoom prevention to the entire app
  disableZoom();
});

onBeforeUnmount(() => {
  // Clean up zoom prevention
  cleanupZoomPrevention(document.body);
});
</script>
